import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const inputCls =
    "w-full border-b border-ivory/20 bg-transparent py-3 text-base text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40";
const ENTER = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8 } };

const LoginForm = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await login(email, password);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-10 space-y-8">
            <div>
                <label htmlFor="admin-email" className={labelCls}>Email</label>
                <input data-testid="admin-email-input" id="admin-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
                <label htmlFor="admin-password" className={labelCls}>Password</label>
                <input data-testid="admin-password-input" id="admin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            {error && <p data-testid="admin-login-error" role="alert" className="text-sm text-terra">{error}</p>}
            <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-night transition-colors duration-300 hover:bg-ivory disabled:opacity-60"
            >
                {loading ? "Signing in…" : "Sign In"}
            </button>
        </form>
    );
};

export default function AdminLogin() {
    const { user } = useAuth();
    if (user) return <Navigate to="/admin" replace />;

    return (
        <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-night px-6 text-ivory">
            <span aria-hidden className="pointer-events-none absolute -left-6 -top-10 select-none font-serif text-[16rem] italic leading-none text-ivory/[0.03] lg:text-[28rem]">
                N
            </span>
            <motion.div {...ENTER} className="relative w-full max-w-md">
                <img src="/brand/logo-transparent.png" alt="Noir Nest" className="h-24 w-auto" />
                <div className="mt-10 flex items-center gap-4">
                    <Lock size={14} className="text-gold" />
                    <span className="text-xs uppercase tracking-[0.25em] text-ivory/50">Studio Access</span>
                </div>
                <h1 className="mt-4 font-serif text-4xl font-light tracking-tight sm:text-5xl">Leads Dashboard</h1>
                <LoginForm />
            </motion.div>
        </div>
    );
}
